<template>
    <div>
        <component :is="usePortal ? 'teleport' : 'div'" to="body">
            <div ref="popupContainer"
                :class="[isTopModal ? (scrollVisible ? 'overflow-y-scroll' : 'overflow-y-auto') : 'overflow-hidden']"
                class="fixed w-screen h-screen inset-0 z-top leading-normal">
                <div
                    ref="container"
                    :class="[(position === 'center' ? 'justify-center' : 'justify-start')]"
                    :style="'padding-right: ' + padding + 'px;'"
                    class="min-h-full flex flex-col items-center relative"
                >
                    <div
                        :class="[
                            (fullSize ? 'flex-grow' : ''),
                            containerClasses || '',
                            position === 'top' ? 'mt-48' : null
                        ]"
                        class="flex flex-col items-center justify-center z-10"
                    >
                        <div :class="[fullSize ? 'flex-grow w-full' : '', customClasses || '', withoutPadding ? '' : 'p-48']"
                             class="bg-white shadow rounded relative my-48">

                            <header
                                class="flex flex-col items-center"
                                :class="textCenter ? 'text-center' : 'text-left'"
                                v-if="title || description || hasCloseButton"
                            >
                                <button v-if="hasCloseButton" class="absolute top-0 right-0 mr-16 mt-16 group"
                                    @click="close">
                                    <svg-icon
                                        class="text-primary-80 group-hover:text-semantic-error trans-ease"
                                        type="cancel"
                                    />
                                </button>

                                <div v-if="title || description"
                                    :class="[titleBorder ? 'border-b-2 border-primary-20 mb-32' : '']" class="w-full">
                                    <div class="mb-32">

                                        <h1 v-if="title"
                                            :class="[description ? 'mb-16' : '', textCenter ? 'justify-center' : 'justify-start']"
                                            class="flex items-center"
                                        >
                                            <svg-icon v-if="icon" :type="icon" class="max-w-24 max-h-24 mr-16">
                                            </svg-icon>
                                            <span class="first-letter:uppercase text-heading-2 font-semibold leading-tight"
                                                v-html="title"></span>
                                        </h1>

                                        <p v-if="description"
                                           class="text-primary-80 max-w-480"
                                           :class="[textCenter ? 'mx-auto' : '']"
                                           v-html="description"></p>
                                    </div>
                                </div>
                            </header>

                            <slot></slot>

                        </div>
                    </div>

                    <div class="w-full h-full bg-neutral-grey opacity-75 absolute inset-0 z-0"
                        @click="closeOnClick ? close() : () => { }">
                    </div>

                </div>
            </div>

            <div v-if="confirm && showConfirmablePopup" class="fixed w-screen h-screen inset-0 z-top leading-normal overflow-y-auto">
                <div  class="min-h-full flex flex-col items-center relative justify-start pt-192" style="padding-right: 0px;">
                    <div class="flex flex-col items-center justify-center z-10">
                        <div class="bg-white shadow-md rounded relative overflow-hidden">
                            <header class="sm:flex sm:items-start p-48">
                                <div class="w-full">
                                    <div class="text-body text-primary-80 sm:mr-32 max-w-480">
                                        {{ $t('general.popup.confirm_close') }}
                                    </div>
                                </div>
                            </header>
                            <div class="bg-primary-5 py-16 px-24 space-y-16 sm:space-y-0 sm:flex sm:flex-row-reverse sm:gap-8">
                                <simple-button
                                    @click="closeAfterConfirm"
                                    display="danger"
                                    look="primary"
                                >
                                    {{ $t('general.confirm') }}
                                </simple-button>
                                <simple-button
                                    @click="showConfirmablePopup = false"
                                >
                                    {{ $t('general.cancel') }}
                                </simple-button>
                            </div>
                        </div>
                    </div>
                    <div @click="showConfirmablePopup = false" class="w-full h-full bg-neutral-grey opacity-75 absolute inset-0 z-0"></div>
                </div>
            </div>

        </component>
    </div>
</template>

<script>
import ResizeObserver from 'resize-observer-polyfill';
import bus from '../../../shared/eventbus';
import SvgIcon from './SvgIcon.vue';
import SimpleButton from "./SimpleButton.vue";
import {mapStores} from "pinia";
import {useMainStore} from "../../state/store";

export default {
    components: {
        SimpleButton,
        SvgIcon
    },
    props: {
        title: String,
        description: String,
        fullSize: { type: Boolean, default: false },
        customClasses: { type: String },
        containerClasses: { type: String},
        hasCloseButton: { type: Boolean, default: true },
        position: {
            type: String,
            validator: value => ['top', 'center'].includes(value),
            default: 'top',
        },
        textCenter: { type: Boolean, default: true },
        closeOnClick: { type: Boolean, default: true },
        dirty: { type: Boolean, default: true },
        icon: { type: String, required: false },
        titleBorder: { type: Boolean, default: true },
        scrollVisible: Boolean,
        withoutPadding: { type: Boolean, default: false },
        usePortal: { type: Boolean, default: true },
        confirm: { type: Boolean, default: false },
    },
    data: function () {
        return {
            scrollbarWidth: 0,
            confirmed: false,
            showConfirmPopup: false,
            showConfirmablePopup: false,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        topModal() {
            return this.mainStore.topModal;
        },
        isTopModal() {
            return this.topModal === this._uid;
        },
        padding() {
            return !this.isTopModal ? this.scrollbarWidth : 0;
        },
    },
    methods: {
        close() {
            if(this.confirm && this.dirty) {
                this.showConfirmablePopup = true;
            } else {
                this.mainStore.resetSelectedLocale();
                this.$emit('close');
            }
        },
        closeAfterConfirm() {
            this.mainStore.resetSelectedLocale();
            this.$emit('close');
        },
        scrollToTop() {
            if (this.$refs.popupContainer) {
                this.$refs.popupContainer.scrollTop = 0;
            }
        },
    },
    beforeMount() {
        this.mainStore.registerModal(this._uid);
    },
    mounted() {
        this.$nextTick(() => {
            let observer = new ResizeObserver((entries, observer) => {
                this.scrollbarWidth = window.innerWidth - entries[0].contentRect.width;
            });

            observer.observe(this.$refs.container);
        });

        bus.addEventListener('popup-top', (e) => {
            if (this.topModal) {
                this.scrollToTop();
            }
        });

        let popup = this;
        window.addEventListener('keyup', (event) => {
            if (event.key === 'Escape' && popup.hasCloseButton) {
                popup.close()
            }
        });
    },
    unmounted() {
        this.mainStore.unregisterModal(this._uid);
    },
};
</script>
<style scoped>
.translatable-content {
    max-width: 620px;
}
</style>
